import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function QuizTaker({ 
  subjectId, 
  gradeLevel, 
  lessonNumber = null, 
  isExam = false,
  isOrdination = false,
  isFinalOrdination = false,
  onComplete,
  onCancel 
}) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(true);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const generateQuiz = useCallback(async () => {
    setGenerating(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      
      // Handle final ordination exam differently
      if (isFinalOrdination) {
        const response = await axios.post(
          `${API}/ordination/exam/generate`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setQuiz(response.data);
        setAnswers(new Array(response.data.questions.length).fill(-1));
        return;
      }
      
      const endpoint = isExam ? "/exam/generate" : "/quiz/generate";
      // Lesson Quiz: 10 questions, Chapter Exam: 25 questions, Ordination/Final: 50 questions
      const payload = isExam 
        ? { subject_id: subjectId, grade_level: gradeLevel, question_count: isOrdination ? 50 : 25 }
        : { subject_id: subjectId, grade_level: gradeLevel, lesson_number: lessonNumber, question_count: 10 };
      
      const response = await axios.post(
        `${API}${endpoint}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setQuiz(response.data);
      setAnswers(new Array(response.data.questions.length).fill(-1));
    } catch (err) {
      const errorDetail = err.response?.data?.detail || "";
      // SAFEGUARD: Check for budget exceeded and show user-friendly message
      if (errorDetail.toLowerCase().includes("budget") || errorDetail.toLowerCase().includes("exceeded")) {
        setError("⚠️ Quiz generation temporarily unavailable. AI budget exceeded. Please try again later or contact support.");
      } else if (errorDetail.toLowerCase().includes("unavailable") || err.response?.status === 503) {
        setError("⚠️ Quiz generation is temporarily unavailable. Please try again in a few minutes.");
      } else {
        setError(errorDetail || "Error generating quiz. Make sure curriculum is generated first.");
      }
    } finally {
      setGenerating(false);
    }
  }, [subjectId, gradeLevel, lessonNumber, isExam, isOrdination, isFinalOrdination]);

  useEffect(() => {
    generateQuiz();
  }, [generateQuiz]);

  const selectAnswer = (questionIndex, optionIndex) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = optionIndex;
    setAnswers(newAnswers);
  };

  const submitQuiz = async () => {
    // Check if all questions are answered
    if (answers.some(a => a === -1)) {
      setError("Please answer all questions before submitting.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const quizId = quiz.quiz_id || quiz.exam_id;
      
      // Use different endpoint for final ordination exam
      const submitEndpoint = isFinalOrdination ? "/ordination/exam/submit" : "/quiz/submit";
      
      const response = await axios.post(
        `${API}${submitEndpoint}`,
        { quiz_id: quizId, answers },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(response.data);
      if (onComplete) {
        onComplete(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Error submitting quiz.");
    } finally {
      setLoading(false);
    }
  };

  if (generating) {
    return (
      <div className="card" data-testid="quiz-loading">
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "3rem", marginBottom: "20px" }}>📝</div>
          <h3 style={{ color: "#FFD700" }}>
            Generating {isExam ? "Exam" : "Quiz"}...
          </h3>
          <p style={{ color: "#aaa" }}>
            Creating questions based on your course material. This takes a moment...
          </p>
        </div>
      </div>
    );
  }

  if (error && !quiz) {
    return (
      <div className="card" data-testid="quiz-error">
        <div className="alert alert-error">{error}</div>
        <button onClick={onCancel} style={{ marginTop: "15px" }}>
          ← Back
        </button>
      </div>
    );
  }

  if (result) {
    const isOrdinationResult = result.ordained !== undefined;
    
    return (
      <div data-testid="quiz-result">
        <div className="card" style={{ 
          background: result.passed ? "#1a4d1a" : "#4d1a1a",
          textAlign: "center"
        }}>
          <div style={{ fontSize: "4rem", marginBottom: "20px" }}>
            {result.ordained ? "🎓" : result.passed ? "🎉" : "📚"}
          </div>
          <h2 style={{ color: result.passed ? "#90EE90" : "#FFD700" }}>
            {result.ordained 
              ? "YOU HAVE BEEN ORDAINED!" 
              : result.passed 
                ? "Congratulations!" 
                : "Keep Studying!"}
          </h2>
          <div style={{ fontSize: "3rem", fontWeight: "bold", margin: "20px 0" }}>
            {result.score}%
          </div>
          <p style={{ fontSize: "1.2rem", color: "#eee" }}>
            {result.correct_answers} out of {result.total_questions} correct
          </p>
          <p style={{ color: "#aaa" }}>
            Passing score: {result.passing_score}%
          </p>
          {result.ordained && (
            <div style={{
              background: "#2a5d2a",
              padding: "20px",
              borderRadius: "8px",
              marginTop: "20px"
            }}>
              <p style={{ color: "#FFD700", fontSize: "1.1rem", margin: 0 }}>
                Your ordination certificate is now available in the Certificate section!
              </p>
            </div>
          )}
        </div>

        {/* Review Answers */}
        <div className="card">
          <h3 style={{ color: "#FFD700", marginBottom: "20px" }}>
            📋 Review Your Answers
          </h3>
          
          {result.questions_with_answers?.map((q, index) => (
            <div
              key={index}
              style={{
                background: q.is_correct ? "#1a4d1a" : "#4d1a1a",
                padding: "20px",
                marginBottom: "15px",
                borderRadius: "8px",
                borderLeft: `4px solid ${q.is_correct ? "#90EE90" : "#ff6b6b"}`
              }}
            >
              <div style={{ marginBottom: "10px", fontWeight: "bold", color: "#eee" }}>
                {index + 1}. {q.question}
              </div>
              
              {q.options.map((option, optIndex) => (
                <div
                  key={optIndex}
                  style={{
                    padding: "8px 12px",
                    margin: "5px 0",
                    borderRadius: "4px",
                    background: 
                      optIndex === q.correct_answer ? "#2a5d2a" :
                      optIndex === q.user_answer && !q.is_correct ? "#5d2a2a" : "#333",
                    border: optIndex === q.correct_answer ? "2px solid #90EE90" : "none",
                    color: "#eee"
                  }}
                >
                  <span style={{ color: optIndex === q.correct_answer ? "#90EE90" : optIndex === q.user_answer && !q.is_correct ? "#ff6b6b" : "#eee" }}>
                    {optIndex === q.correct_answer && "✓ "}
                    {optIndex === q.user_answer && optIndex !== q.correct_answer && "✗ "}
                  </span>
                  {option}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="card" style={{ background: "#0a0a0a" }}>
          <button onClick={onCancel} style={{ width: "100%" }}>
            ← Back to Course
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="quiz-taker">
      {/* Quiz Header */}
      <div className="card" style={isFinalOrdination ? {
        background: "linear-gradient(135deg, #1a1a1a 0%, #2a1a2a 100%)",
        borderTop: "4px solid #FFD700"
      } : {}}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ color: "#FFD700", margin: "0 0 5px 0" }}>
              {isFinalOrdination 
                ? "✝️ Final Ordination Examination"
                : isOrdination 
                  ? `📝 Ordination Course Exam`
                  : isExam 
                    ? "📝 Chapter Exam" 
                    : `📝 Lesson ${lessonNumber} Quiz`}
            </h3>
            <p style={{ margin: 0, color: "#aaa" }}>
              {quiz?.subject || (isFinalOrdination ? "Comprehensive Ministry Exam" : subjectId)} • {gradeLevel}
            </p>
            {(isOrdination || isFinalOrdination) && (
              <p style={{ margin: "5px 0 0 0", color: "#FFD700", fontSize: "0.9rem" }}>
                Passing score: {isFinalOrdination ? "85%" : "80%"}
              </p>
            )}
          </div>
          <div style={{ 
            background: "#1a1a1a", 
            padding: "10px 20px", 
            borderRadius: "8px",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#FFD700" }}>
              {answers.filter(a => a !== -1).length}/{quiz?.questions?.length || 0}
            </div>
            <div style={{ fontSize: "0.8rem", color: "#aaa" }}>Answered</div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Questions */}
      {quiz?.questions?.map((q, qIndex) => (
        <div 
          key={qIndex} 
          className="card"
          style={{ 
            background: answers[qIndex] !== -1 ? "#1a2a1a" : "#1a1a1a",
            borderLeft: `4px solid ${answers[qIndex] !== -1 ? "#90EE90" : "#FFD700"}`
          }}
        >
          <div style={{ marginBottom: "15px" }}>
            <span style={{ 
              background: "#FFD700", 
              color: "#000", 
              padding: "4px 10px", 
              borderRadius: "4px",
              fontWeight: "bold",
              marginRight: "10px"
            }}>
              {qIndex + 1}
            </span>
            <span style={{ fontSize: "1.1rem", color: "#eee" }}>{q.question}</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {q.options.map((option, optIndex) => (
              <button
                key={optIndex}
                onClick={() => selectAnswer(qIndex, optIndex)}
                data-testid={`q${qIndex}-option${optIndex}`}
                style={{
                  padding: "15px",
                  textAlign: "left",
                  background: answers[qIndex] === optIndex ? "#2a5d2a" : "#222",
                  border: answers[qIndex] === optIndex ? "2px solid #90EE90" : "2px solid #333",
                  marginTop: 0,
                  color: "#eee"
                }}
              >
                <span style={{ 
                  marginRight: "10px",
                  color: answers[qIndex] === optIndex ? "#90EE90" : "#FFD700"
                }}>
                  {String.fromCharCode(65 + optIndex)}.
                </span>
                {option}
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Submit Button */}
      <div className="card" style={{ background: "#0a0a0a" }}>
        <div style={{ display: "flex", gap: "15px" }}>
          <button
            onClick={onCancel}
            style={{ flex: 1, background: "#333" }}
          >
            Cancel
          </button>
          <button
            onClick={submitQuiz}
            disabled={loading || answers.some(a => a === -1)}
            data-testid="submit-quiz-btn"
            style={{ 
              flex: 2,
              background: answers.every(a => a !== -1) ? "#FFD700" : "#666",
              color: answers.every(a => a !== -1) ? "#000" : "#aaa"
            }}
          >
            {loading ? "Submitting..." : `Submit ${isExam ? "Exam" : "Quiz"}`}
          </button>
        </div>
        <p style={{ textAlign: "center", color: "#aaa", marginTop: "10px", marginBottom: 0 }}>
          {answers.filter(a => a === -1).length > 0 
            ? `Answer ${answers.filter(a => a === -1).length} more question(s) to submit`
            : "Ready to submit!"}
        </p>
      </div>
    </div>
  );
}

export default QuizTaker;
